import 'ol/ol.css';
import { apply as applyMapboxStyle } from 'ol-mapbox-style';
import Map from 'ol/Map';
import View from 'ol/View';
import { useGeographic as olUseGeographic } from 'ol/proj';
import {
  DragRotateAndZoom,
  defaults as defaultInteractions
} from 'ol/interaction.js';
import TileLayer from 'ol/layer/Tile';
import { Style, Stroke, Fill } from 'ol/style';
import VectorLayer from 'ol/layer/Vector';
import { Vector as VectorSource } from 'ol/source';
import { GeoJSON, WMTSCapabilities } from 'ol/format';
import WMTS, { optionsFromCapabilities } from 'ol/source/WMTS';
import Overlay from 'ol/Overlay';

olUseGeographic();

const YEAR_CONFIG = [
  { value: 2026, label: '2026' },
  { value: 2025, label: '2025' },
  { value: 2024, label: '2024' },
  { value: 2023, label: 'Overall Ranking' },
];

const styles = {
  tracksStyle: new Style({
    stroke: new Stroke({
      color: 'rgb(226, 84, 33)',
      width: 2.75
    })
  }),
  checkpointStyle: new Style({
    stroke: new Stroke({
      color: 'rgb(34, 197, 94)',
      width: 2.25
    }),
    fill: new Fill({
      color: 'rgba(34, 197, 94, 0.18)'
    })
  }),
  bundeslaenderStyle: new Style({
    stroke: new Stroke({
      color: '#708090',
      width: 1
    }),
    fill: new Fill({
      color: 'rgba(112, 128, 144, 0.35)'
    })
  }),
  bufferStyle: new Style({
    stroke: new Stroke({
      color: 'rgb(59, 130, 246)',
      width: 2.5
    })
  }),
  highlightTrack: new Style({
    stroke: new Stroke({
      color: '#2563eb',
      width: 4
    })
  }),
  highlightCheckpoint: new Style({
    stroke: new Stroke({
      color: '#10b981',
      width: 3
    }),
    fill: new Fill({
      color: 'rgba(16, 185, 129, 0.25)'
    })
  }),
  highlightBuffer: new Style({
    stroke: new Stroke({
      color: 'rgb(59, 130, 246)',
      width: 3
    }),
    fill: new Fill({
      color: 'rgba(59, 130, 246, 0.25)'
    })
  })
};

const cacheBustUrl = (url) => {
  const joiner = url.includes('?') ? '&' : '?';
  return `${url}${joiner}cb=${Date.now()}`;
};

const createVectorLayer = (url, style, zIndex) => new VectorLayer({
  source: new VectorSource({
    url: cacheBustUrl(url),
    format: new GeoJSON(),
  }),
  style,
  zIndex
});

const checkpointsPoly = createVectorLayer('../json/checkpoints_poly.geojson', styles.bufferStyle, 2);
const states = createVectorLayer('../json/states.geojson', styles.bundeslaenderStyle, 1);

const mapCenter = [11.973501901767666, 51.48848410657496];
const map = new Map({
  interactions: defaultInteractions().extend([new DragRotateAndZoom()]),
  view: new View({
    center: mapCenter,
    zoom: 8
  }),
  target: 'map'
});

applyMapboxStyle(map, 'https://sgx.geodatenzentrum.de/gdz_basemapde_vektor/styles/bm_web_gry.json');

const addWMTSLayer = () => {
  fetch('https://mapsneu.wien.gv.at/basemapneu/1.0.0/WMTSCapabilities.xml')
    .then(response => response.text())
    .then(text => {
      const parser = new WMTSCapabilities();
      const result = parser.read(text);
      const options = optionsFromCapabilities(result, {
        layer: 'bmapgrau',
        matrixSet: 'google3857'
      });

      const wmtsLayer = new TileLayer({
        source: new WMTS(options)
      });

      map.addLayer(wmtsLayer);
    })
    .catch(error => console.log('Fehler beim Laden der WMTS-Capabilities:', error));
};

addWMTSLayer();
map.addLayer(checkpointsPoly);
map.addLayer(states);

const form = document.getElementById('controlForm');
const riderInput = document.getElementById('riderId');
const yearSelect = document.getElementById('yearSelect');
const loadButton = document.getElementById('loadButton');
const resetButton = document.getElementById('resetButton');
const spinner = document.getElementById('spinner');
const statusMessage = document.getElementById('statusMessage');
const infoButton = document.getElementById('infoButton');
const infoPopup = document.getElementById('infoPopup');
const tableButton = document.getElementById('tableButton');
const tableContainer = document.getElementById('tableContainer');
const tableContent = document.getElementById('tableContent');
const closeTable = document.getElementById('closeTable');
const statsYearSelect = document.getElementById('statsYearSelect');
const rowPopup = document.getElementById('rowPopup');
const rowPopupText = document.getElementById('rowPopupText');
const rowConfirm = document.getElementById('rowConfirm');
const rowCancel = document.getElementById('rowCancel');
const dataError = document.getElementById('dataError');
const dataErrorBack = document.getElementById('dataErrorBack');
const mapPopup = document.getElementById('mapPopup');
const introScreen = document.getElementById('introScreen');
const mapEl = document.getElementById('map');
const fabStack = document.getElementById('fabStack');
const fabReset = document.getElementById('fabReset');
const loadingOverlay = document.getElementById('loadingOverlay');

const setStatus = (message, tone = 'info') => {
  statusMessage.textContent = message;
  statusMessage.dataset.tone = tone;
};

const setLoading = (isLoading) => {
  loadButton.disabled = isLoading;
  spinner.classList.toggle('is-visible', isLoading);
  loadingOverlay.classList.toggle('is-visible', isLoading);
  loadingOverlay.setAttribute('aria-hidden', String(!isLoading));
};

YEAR_CONFIG.forEach(({ value, label }) => {
  const option = document.createElement('option');
  option.value = value;
  option.textContent = label;
  yearSelect.appendChild(option);
});

// Stats dropdown mirrors available years + overall
YEAR_CONFIG.forEach(({ value, label }) => {
  const option = document.createElement('option');
  option.value = value;
  option.textContent = label;
  statsYearSelect.appendChild(option);
});

let activeLayers = null;
let hasStarted = false;
let previousActiveLayers = null;
let highlightedFeature = null;
let highlightLayer = null;

const popupOverlay = new Overlay({
  element: mapPopup,
  positioning: 'bottom-center',
  offset: [0, -10],
});
map.addOverlay(popupOverlay);

const clearActiveLayers = () => {
  if (activeLayers) {
    map.removeLayer(activeLayers.tracksLayer);
    map.removeLayer(activeLayers.checkpointsLayer);
    activeLayers = null;
  }
  previousActiveLayers = null;
};

const clearHighlight = () => {
  if (highlightedFeature) {
    highlightedFeature.setStyle(null);
    highlightedFeature = null;
    highlightLayer = null;
  }
  popupOverlay.setPosition(undefined);
  mapPopup.style.display = 'none';
};

const showMapUI = () => {
  introScreen.classList.add('hidden');
  mapEl.style.visibility = 'visible';
  fabStack.style.display = 'grid';
  document.body.classList.remove('intro-open');
};

const showIntro = () => {
  introScreen.classList.remove('hidden');
  fabStack.style.display = 'none';
  mapEl.style.visibility = 'hidden';
  document.body.classList.add('intro-open');
};

const fitToLayer = (layer) => {
  const extent = layer.getSource().getExtent();
  if (extent && extent.every(Number.isFinite)) {
    const bufferedExtent = [
      extent[0] - 0.02 * (extent[2] - extent[0]),
      extent[1] - 0.02 * (extent[3] - extent[1]),
      extent[2] + 0.02 * (extent[2] - extent[0]),
      extent[3] + 0.02 * (extent[3] - extent[1]),
    ];
    map.getView().fit(bufferedExtent, { duration: 750, maxZoom: 13, padding: [20, 20, 20, 20] });
  }
};

const loadRiderData = (riderId, year) => {
  const tracksLayer = createVectorLayer(`../json/tracks_${year}/${riderId}_tracks.geojson`, styles.tracksStyle, 3);
  const checkpointsLayer = createVectorLayer(`../json/checkpoints_${year}/${riderId}_checkpoints.geojson`, styles.checkpointStyle, 3);

  const handleError = () => {
    setLoading(false);
    setStatus(`Keine Daten für ${riderId} in ${year} gefunden.`, 'error');
    dataError.classList.add('is-visible');
    if (previousActiveLayers) {
      map.addLayer(previousActiveLayers.tracksLayer);
      map.addLayer(previousActiveLayers.checkpointsLayer);
      activeLayers = previousActiveLayers;
      yearSelect.value = previousActiveLayers.year;
      riderInput.value = previousActiveLayers.riderId || riderInput.value;
      previousActiveLayers = null;
    } else {
      clearActiveLayers();
    }
    clearHighlight();
  };

  tracksLayer.getSource().once('change', () => {
    const state = tracksLayer.getSource().getState();
    if (state === 'ready') {
      fitToLayer(tracksLayer);
      setLoading(false);
      setStatus(`Zeige ${year} für Startnummer ${riderId}.`, 'success');
    } else if (state === 'error') {
      handleError();
    }
  });

  tracksLayer.getSource().on('error', handleError);
  checkpointsLayer.getSource().on('error', handleError);

  checkpointsLayer.getSource().once('change', () => {
    const state = checkpointsLayer.getSource().getState();
    if (state === 'error') {
      handleError();
    }
  });

  map.addLayer(tracksLayer);
  map.addLayer(checkpointsLayer);
  activeLayers = { tracksLayer, checkpointsLayer, riderId, year };
  statsYearSelect.value = String(year);
  previousActiveLayers = null;
  clearHighlight();
};

const submitSelection = () => {
  previousActiveLayers = activeLayers;
  const riderId = riderInput.value.trim();
  const year = yearSelect.value;

  if (!riderId) {
    setStatus('Bitte eine Startnummer eintragen.', 'error');
    return;
  }

  setLoading(true);
  clearActiveLayers();
  loadRiderData(riderId, year);
  if (!hasStarted) {
    hasStarted = true;
    showMapUI();
  }
};

const handleFormSubmit = (event) => {
  event.preventDefault();
  submitSelection();
};

const resetSelection = () => {
  riderInput.value = '';
  clearActiveLayers();
  setStatus('Bereit für neue Startnummer.', 'info');
  hasStarted = false;
  showIntro();
};

const recenterMap = () => {
  map.getView().animate({ center: mapCenter, zoom: 8, duration: 500 });
};

form.addEventListener('submit', handleFormSubmit);
resetButton.addEventListener('click', resetSelection);

yearSelect.addEventListener('change', () => {
  if (riderInput.value.trim()) {
    submitSelection();
  }
});

const toggleInfo = () => {
  infoPopup.classList.toggle('is-visible');
};

infoButton.addEventListener('click', toggleInfo);

document.addEventListener('click', (event) => {
  if (!infoPopup.contains(event.target) && !infoButton.contains(event.target)) {
    infoPopup.classList.remove('is-visible');
  }
});

fabReset.addEventListener('click', resetSelection);

let pendingStatsYear = null;
let pendingRowSelection = null;
const statsFiles = {
  2023: '../stats/eisenbahn_stats.csv',
  2024: '../stats/eisenbahn_stats_2024.csv',
  2025: '../stats/eisenbahn_stats_2025.csv',
  2026: '../stats/eisenbahn_stats_2026.csv',
};
const pickStatsYear = (preferred) => {
  const preferredStr = preferred ? String(preferred) : '';
  if (statsFiles[preferredStr]) return preferredStr;
  const sorted = Object.keys(statsFiles).sort((a, b) => Number(b) - Number(a));
  return sorted[0];
};

const parseCSV = (data) => {
  const lines = data.split('\n').map(line => line.trim()).filter(Boolean);
  const [headerLine, ...rows] = lines;
  const headers = headerLine.split(',').map((h) => {
    switch (h) {
      case 'rank': return 'Rang';
      case 'rider_id': return 'Startnummer';
      case 'name': return 'Name';
      case 'netto': return 'Bahnhöfe';
      default: return h;
    }
  });
  const body = rows.map(row => row.split(','));
  return { headers, body };
};

const renderTable = (data) => {
  const { headers, body } = data;
  tableContent.innerHTML = `
    <table>
      <thead>
        <tr>${headers.map(h => `<th>${h}</th>`).join('')}</tr>
      </thead>
      <tbody>
        ${body.map(row => {
          const riderId = row[1] || '';
          const name = row[2] || '';
          return `<tr data-rider="${riderId}" data-name="${name}">${row.map(cell => `<td>${cell}</td>`).join('')}</tr>`;
        }).join('')}
      </tbody>
    </table>
    <p class="table-note">Ranking basiert auf den Jahres-Rankings (alle Jahre zählen separat).</p>
  `;
};

const fetchTextNoCache = async (url) => {
  const response = await fetch(cacheBustUrl(url), { cache: 'no-store' });
  return response.text();
};

const loadStats = async (yearKey) => {
  if (!statsFiles[yearKey]) {
    tableContent.innerHTML = '<p class="table-note">Kein Ranking für dieses Jahr.</p>';
    return;
  }

  try {
    const data = await fetchTextNoCache(statsFiles[yearKey]);
    const parsed = parseCSV(data);
    renderTable(parsed);
  } catch (error) {
    tableContent.innerHTML = '<p class="table-note">Konnte Ranking nicht laden.</p>';
  }
};

tableButton.addEventListener('click', async () => {
  const selectedYear = pickStatsYear((activeLayers?.year && String(activeLayers.year)) || yearSelect.value);
  statsYearSelect.value = selectedYear;
  await loadStats(selectedYear);
  tableContainer.classList.add('is-visible');
  fabStack.style.display = 'none';
});

closeTable.addEventListener('click', () => {
  tableContainer.classList.remove('is-visible');
  fabStack.style.display = hasStarted ? 'grid' : 'none';
  const nextYear = pickStatsYear(pendingStatsYear || (activeLayers?.year && String(activeLayers.year)) || null);
  const riderId = activeLayers?.riderId || riderInput.value.trim();
  if (nextYear && riderId) {
    yearSelect.value = nextYear;
    previousActiveLayers = activeLayers;
    setLoading(true);
    clearActiveLayers();
    loadRiderData(riderId, nextYear);
  }
  pendingStatsYear = null;
});

statsYearSelect.addEventListener('change', async () => {
  const selectedYear = pickStatsYear(statsYearSelect.value);
  pendingStatsYear = selectedYear;
  await loadStats(selectedYear);
});

tableContent.addEventListener('click', (event) => {
  const row = event.target.closest('tr[data-rider]');
  if (!row) return;
  const riderId = row.dataset.rider;
  const riderName = row.dataset.name || '';
  if (!riderId) return;
  pendingRowSelection = {
    riderId,
    name: riderName,
    year: statsYearSelect.value || pickStatsYear(null)
  };
  rowPopupText.textContent = `Rider ${riderId}${riderName ? ` · ${riderName}` : ''} zur Karte laden?`;
  rowPopup.classList.add('is-visible');
});

const clearRowPopup = () => {
  rowPopup.classList.remove('is-visible');
};

rowCancel.addEventListener('click', () => {
  clearRowPopup();
  pendingRowSelection = null;
});

rowConfirm.addEventListener('click', () => {
  if (!pendingRowSelection) return;
  const { riderId, year } = pendingRowSelection;
  riderInput.value = riderId;
  const targetYear = pickStatsYear(year);
  yearSelect.value = targetYear;
  clearRowPopup();
  tableContainer.classList.remove('is-visible');
  fabStack.style.display = hasStarted ? 'grid' : 'none';
  previousActiveLayers = activeLayers;
  setLoading(true);
  clearActiveLayers();
  loadRiderData(riderId, targetYear);
  if (!hasStarted) {
    hasStarted = true;
    showMapUI();
  }
  pendingRowSelection = null;
});

dataErrorBack.addEventListener('click', () => {
  dataError.classList.remove('is-visible');
});

map.on('singleclick', (event) => {
  if (!activeLayers) {
    clearHighlight();
    return;
  }
  let target = null;
  map.forEachFeatureAtPixel(event.pixel, (feature, layer) => {
    if (layer === activeLayers.tracksLayer || layer === activeLayers.checkpointsLayer || layer === checkpointsPoly) {
      target = { feature, layer };
      return true;
    }
    return false;
  }, { hitTolerance: 5 });

  clearHighlight();

  if (!target) {
    return;
  }

  highlightedFeature = target.feature;
  highlightLayer = target.layer;

  if (target.layer === activeLayers.tracksLayer) {
    highlightedFeature.setStyle(styles.highlightTrack);
  } else if (target.layer === checkpointsPoly) {
    highlightedFeature.setStyle(styles.highlightBuffer);
  } else {
    highlightedFeature.setStyle(styles.highlightCheckpoint);
  }

  const geometry = highlightedFeature.getGeometry();
  if (geometry) {
    const extent = geometry.getExtent();
    map.getView().fit(extent, { duration: 600, maxZoom: 15, padding: [30, 30, 30, 30] });
    popupOverlay.setPosition(event.coordinate);
    mapPopup.style.display = 'block';
  }
});
